import React, { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Contacts.scss";
import ContactMeForm from "../../components/ContactMeForm/ContactMeForm";
import { MetaData } from "../../helpers/MetaData";
import logoPhotographer from "../../images/logo-header-photographer.webp";
import JsonLd from "../../helpers/JsonLD";
import { socialPreviews } from "../../utils/socialPreviews";
import { Map } from "../../components/Map/Map";
import { charlottePhotographyServiceAreas } from "../../utils/seo";

const Contacts: React.FC = () => {
  const { pathname } = useLocation();

  const pageUrl = `https://alenalobacheva.com${pathname}`;
  const pageTitle = "Contact Charlotte Newborn & Family Photographer | Alena Lobacheva";
  const pageDescription =
    "Contact Alena Lobacheva Photography with questions about newborn, maternity, baby, or family photography in Charlotte, NC and surrounding areas.";

  const webPageDataContacts = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${pageUrl}#contact-page`,
    name: pageTitle,
    description: pageDescription,
    image: socialPreviews.contact.imageOG,
    url: pageUrl,
  };

  const localBusinessDataContacts = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://alenalobacheva.com/#localbusiness",
    name: "Alena Lobacheva Photography",
    description: "Professional newborn, maternity, baby, and family photographer based in Charlotte, North Carolina.",
    image: socialPreviews.contact.imageOG,
    url: "https://alenalobacheva.com/",
    email: "lobachevaphotography@gmail.com",
    telephone: "+1-516-468-4837",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Charlotte",
      addressRegion: "NC",
      addressCountry: "US",
    },
    areaServed: charlottePhotographyServiceAreas,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-516-468-4837",
      email: "lobachevaphotography@gmail.com",
      contactType: "customer service",
      availableLanguage: ["English", "Russian"],
    },
    sameAs: [
      "https://www.instagram.com/lobachevaphotography/",
      "https://www.facebook.com/Alen4ikLobacheva?mibextid=9R9pXO",
      "https://www.tiktok.com/@lobachevaphotography/",
    ],
  };

  return (
    <Fragment>
      <JsonLd data={webPageDataContacts} />
      <JsonLd data={localBusinessDataContacts} />
      <MetaData
        title={pageTitle}
        description={pageDescription}
        canonicalLink={pageUrl}
        {...socialPreviews.contact}
        imageAltOG="Family portrait photography by Alena Lobacheva in Charlotte, North Carolina"
        titleOG={pageTitle}
        descriptionOG={pageDescription}
      />

      <main className="contacts">
        <header className="contacts__intro">
          <p className="contacts__eyebrow">Contact Alena</p>
          <h1 className="contacts__title">Let&apos;s talk about what you are looking for</h1>
          <p className="contacts__introText">
            Have a question or simply want to say hello? Send me a message—I&apos;d love to hear from you.
          </p>
        </header>

        <section className="contacts__main" aria-label="Contact Alena Lobacheva">
          <div className="contacts__details">
            <h2 className="contacts__sectionTitle">A simple conversation is the best place to start.</h2>
            <p className="contacts__bodyText">
              You don&apos;t need to choose a package, know a session date, or have everything planned. Tell me
              what&apos;s on your mind, and I&apos;ll help with the rest.
            </p>
            <img
              className="contacts__signature"
              src={logoPhotographer}
              width="283"
              height="80"
              alt="Alena Lobacheva Photography"
            />
            <p className="contacts__responseTime">I respond to messages every day, usually within 24 hours.</p>

            <div className="contacts__direct">
              <p className="contacts__directTitle">Prefer to reach me directly?</p>
              <a className="contacts__directLink" href="mailto:lobachevaphotography@gmail.com">
                lobachevaphotography@gmail.com
              </a>
              <a className="contacts__directLink" href="tel:+15164684837">
                Call or text: +1 (516) 468-4837
              </a>
              <a
                className="contacts__directLink"
                href="https://wa.me/15164684837"
                target="_blank"
                rel="noopener noreferrer"
                title="Message Alena on WhatsApp"
                aria-label="Message me on WhatsApp"
              >
                Message me on WhatsApp <span aria-hidden="true">↗</span>
              </a>
              <p className="contacts__location">Charlotte, NC and surrounding areas</p>
            </div>

            <Link className="contacts__aboutLink" to="/about/">
              About Alena <span aria-hidden="true">→</span>
            </Link>
          </div>

          <ContactMeForm title="Get in touch" />
        </section>

        <section className="contacts__process" aria-labelledby="contact-process-title">
          <p className="contacts__eyebrow">Simple &amp; personal</p>
          <h2 className="contacts__sectionTitle" id="contact-process-title">
            What happens next
          </h2>
          <div className="contacts__steps">
            <article className="contacts__step">
              <span className="contacts__stepNumber">01</span>
              <h3 className="contacts__stepTitle">Write freely</h3>
              <p className="contacts__stepText">No booking details are required.</p>
            </article>
            <article className="contacts__step">
              <span className="contacts__stepNumber">02</span>
              <h3 className="contacts__stepTitle">Hear from Alena</h3>
              <p className="contacts__stepText">Receive a personal reply, not an automated sales sequence.</p>
            </article>
            <article className="contacts__step">
              <span className="contacts__stepNumber">03</span>
              <h3 className="contacts__stepTitle">Decide together</h3>
              <p className="contacts__stepText">If a session feels right, planning can begin afterward.</p>
            </article>
          </div>
        </section>

        <section className="contacts__instagram" aria-labelledby="contact-instagram-title">
          <div>
            <p className="contacts__eyebrow">Recent work</p>
            <h2 className="contacts__sectionTitle" id="contact-instagram-title">
              Follow along on Instagram
            </h2>
            <p className="contacts__bodyText">See recent newborn, maternity, baby, and family sessions.</p>
          </div>
          <a
            className="contacts__instagramLink"
            href="https://www.instagram.com/lobachevaphotography/"
            target="_blank"
            rel="noopener noreferrer"
          >
            @lobachevaphotography <span aria-hidden="true">→</span>
          </a>
        </section>

        <section className="contacts__serviceArea" aria-labelledby="contact-service-area-title">
          <div className="contacts__serviceAreaCopy">
            <p className="contacts__eyebrow">Service area</p>
            <h2 className="contacts__sectionTitle" id="contact-service-area-title">
              Photographing families across Charlotte
            </h2>
            <p className="contacts__bodyText">
              Serving Charlotte and surrounding areas across North and South Carolina. Exact session locations are
              selected together.
            </p>
            <p className="contacts__travelText">
              Travel sessions in Asheville, Greenville, Greensboro, and the Raleigh–Durham area are available by
              request.
            </p>
          </div>
          <Map />
        </section>
      </main>
    </Fragment>
  );
};

export default Contacts;
